import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, CheckCircle, Activity, Zap, Database, FileDown, ChevronRight, Gauge } from 'lucide-react';
import * as THREE from 'three';

const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [credentials, setCredentials] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const { login, sign_up, forgotPassword } = useAuth();
  const navigate = useNavigate();
  
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    // Three.js setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 15;
    camera.position.y = 5;
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    canvasRef.current?.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create login form visualization
    const formGeometry = new THREE.BoxGeometry(10, 14, 0.5);
    const formMaterial = new THREE.MeshPhongMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.2,
      wireframe: true
    });
    const loginForm = new THREE.Mesh(formGeometry, formMaterial);
    scene.add(loginForm);

    // Add floating particles
    const particleGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const particleMaterial = new THREE.MeshPhongMaterial({ color: 0x00ff00 });
    const particles = [];

    for (let i = 0; i < 100; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      particle.position.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 20
      );
      particle.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 0.05,
        (Math.random() - 0.5) * 0.05,
        (Math.random() - 0.5) * 0.05
      );
      particles.push(particle);
      scene.add(particle);
    }

    // Add glowing lines
    const lineMaterial = new THREE.LineBasicMaterial({ 
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3
    });
    const lines = [];

    for (let i = 0; i < 20; i++) {
      const lineGeometry = new THREE.BufferGeometry();
      const points = [
        new THREE.Vector3(
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20
        ),
        new THREE.Vector3(
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20
        )
      ];
      lineGeometry.setFromPoints(points);
      const line = new THREE.Line(lineGeometry, lineMaterial);
      lines.push(line);
      scene.add(line);
    }

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0x3b82f6, 2);
    pointLight1.position.set(10, 10, 10);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x00ff00, 1);
    pointLight2.position.set(-10, -10, -10);
    scene.add(pointLight2);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      // Rotate form slightly
      loginForm.rotation.y = Math.sin(Date.now() * 0.001) * 0.1;
      loginForm.rotation.x = Math.cos(Date.now() * 0.001) * 0.1;

      // Animate particles
      particles.forEach(particle => {
        particle.position.add(particle.velocity);
        
        // Boundary check and bounce
        ['x', 'y', 'z'].forEach(axis => {
          if (Math.abs(particle.position[axis]) > 10) {
            particle.velocity[axis] *= -1;
          }
        });
      });

      // Animate lines
      lines.forEach((line, i) => {
        line.material.opacity = 0.3 + Math.sin(Date.now() * 0.001 + i) * 0.2;
      });

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
      
      // Adjust camera position based on screen size
      if (width < 768) { // Mobile
        camera.position.z = 20;
        camera.position.y = 3;
      } else if (width < 1024) { // Tablet
        camera.position.z = 17;
        camera.position.y = 4;
      } else { // Desktop
        camera.position.z = 15;
        camera.position.y = 5;
      }
      
      camera.lookAt(0, 0, 0);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial setup

    return () => {
      window.removeEventListener('resize', handleResize);
      canvasRef.current?.removeChild(renderer.domElement);
      renderer.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!isLogin && !validateEmail(credentials.email)) {
      setNotification({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setIsSubmitting(true);
    setNotification(null);
    try {
      if (isForgotPassword) {
        await forgotPassword(credentials.email);
        setNotification({ type: 'success', message: 'Please check your email for the new password.' });
        setIsForgotPassword(false);
      } else if (isLogin) {
        await login({ email: credentials.email, password: credentials.password });
        navigate('/');
      } else {
        await sign_up(credentials);
        navigate('/');
      }
    } catch (error) {
      if (error.message === 'Invalid credentials.') {
        setNotification({ type: 'error', message: 'Invalid email or password.' });
      } else if (error.message === 'User account not found') {
        setNotification({ type: 'error', message: 'No account found with this email address.' });
      } else if (error.message === 'Account already exists') {
        setNotification({ type: 'error', message: 'An account with this email already exists.' });
      } else {
        setNotification({ type: 'error', message: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <div ref={canvasRef} className="fixed inset-0 pointer-events-none" />
      
      {/* Left side - Product Information */}
      <div className="w-full lg:w-2/3 p-4 sm:p-6 lg:p-12 flex flex-col justify-center overflow-y-auto relative z-10">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-100 text-center">
            Intelligent Building Management System Analytics
          </h1>
          
          <p className="text-lg sm:text-xl mb-8 sm:mb-12 text-blue-100 leading-relaxed text-center sm:text-left">
            Transform your building management with real-time analytics and intelligent monitoring for your BMS infrastructure.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
            <div className="bg-white/10 backdrop-blur-lg p-4 sm:p-6 rounded-xl border border-blue-400/30 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-3 sm:mb-4">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mr-2 sm:mr-3" />
                <h3 className="text-lg sm:text-xl font-semibold text-blue-100">Real-Time Monitoring</h3>
              </div>
              <p className="text-sm sm:text-base text-blue-200">Monitor 200,000+ data points per site with ultra-low latency updates (&lt; 2ms) for immediate insights into your building's performance.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg p-4 sm:p-6 rounded-xl border border-blue-400/30 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-3 sm:mb-4">
                <Gauge className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mr-2 sm:mr-3" />
                <h3 className="text-lg sm:text-xl font-semibold text-blue-100">Quality Assurance</h3>
              </div>
              <p className="text-sm sm:text-base text-blue-200">Advanced OPC data point quality monitoring and stagnant data detection to ensure your BMS is operating at peak efficiency.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg p-4 sm:p-6 rounded-xl border border-blue-400/30 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-3 sm:mb-4">
                <FileDown className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mr-2 sm:mr-3" />
                <h3 className="text-lg sm:text-xl font-semibold text-blue-100">Comprehensive Reports</h3>
              </div>
              <p className="text-sm sm:text-base text-blue-200">Generate detailed reports on data quality statistics and stagnant point analysis to maintain optimal system health.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg p-4 sm:p-6 rounded-xl border border-blue-400/30 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-3 sm:mb-4">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mr-2 sm:mr-3" />
                <h3 className="text-lg sm:text-xl font-semibold text-blue-100">Future-Ready</h3>
              </div>
              <p className="text-sm sm:text-base text-blue-200">Continuous development of intelligent features for enhanced monitoring and predictive maintenance of your sensing and control infrastructure.</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-base sm:text-lg text-blue-100 mb-4">
              Perfect for smart buildings, factories, and data centers requiring enterprise-grade monitoring solutions.
            </p>
            <a 
              href="https://tetraagroup.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-300"
            >
              Learn more about Tetraa Group
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Right side - Login/Register Form */}
      <div className="w-full lg:w-1/3 relative flex flex-col min-h-[600px] lg:min-h-screen bg-gradient-to-br from-blue-900/50 to-blue-800/50 backdrop-blur-xl overflow-hidden z-10">
        <div className="relative flex-grow flex items-center justify-center p-4">
          <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-xl p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md border border-blue-400/30">
            <div className="mb-6 sm:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-100">Tetraa INTelligence</h2>
              <p className="text-sm sm:text-base text-blue-200 mt-2">by Tetraa Group</p>
            </div>
            
            {!isLogin && !isForgotPassword && (
              <input
                type="text"
                name="name"
                value={credentials.name}
                onChange={handleChange}
                placeholder="Full Name"
                required
                className="w-full p-2.5 sm:p-3 mb-4 border border-blue-400/30 rounded-lg bg-white/10 text-blue-100 placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-sm sm:text-base"
              />
            )}
            <input
              type="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="Email Address"
              required
              className="w-full p-2.5 sm:p-3 mb-4 border border-blue-400/30 rounded-lg bg-white/10 text-blue-100 placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-sm sm:text-base"
            />
            {!isForgotPassword && (
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Password"
                  required
                  className="w-full p-2.5 sm:p-3 mb-6 border border-blue-400/30 rounded-lg pr-10 bg-white/10 text-blue-100 placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-sm sm:text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-300 hover:text-blue-200"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4 sm:h-5 sm:w-5 mb-5" />
                  ) : (
                    <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 mb-5" />
                  )}
                </button>
              </div>
            )}
            <button 
              type="submit" 
              className={`w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 sm:p-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition-all duration-300 text-sm sm:text-base ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : (isForgotPassword ? 'Reset Password' : isLogin ? 'Sign In' : 'Create Account')}
            </button>

            {notification && (
              <div className={`mt-4 p-2.5 sm:p-3 text-center text-xs sm:text-sm rounded-lg ${notification.type === 'error' ? 'bg-red-900/50 text-red-200' : 'bg-green-900/50 text-green-200'}`}>
                {notification.message}
              </div>
            )}

            <div className="mt-6 text-center space-y-2">
              {!isForgotPassword && (
                <p className="text-xs sm:text-sm text-blue-200">
                  {isLogin ? "Don't have an account? " : "Already have an account? "}
                  <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-blue-400 hover:underline font-semibold">
                    {isLogin ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              )}
              {isLogin && !isForgotPassword && (
                <p className="text-xs sm:text-sm">
                  <button type="button" onClick={() => setIsForgotPassword(true)} className="text-blue-400 hover:underline font-semibold">
                    Forgot Password?
                  </button>
                </p>
              )}
              {isForgotPassword && (
                <p className="text-xs sm:text-sm">
                  <button type="button" onClick={() => setIsForgotPassword(false)} className="text-blue-400 hover:underline font-semibold">
                    Back to Sign In
                  </button>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;